import { AnyTranslationsFn } from "@/types/locale";
import { z, ZodObject, ZodString, ZodURL } from "zod/v4";
import { contentTools } from "@lib/content";

/**
 * Business-oriented Zod helpers to:
 * - Centralize common validation rules for forms and DTOs.
 * - Produce localized error messages via the provided translation function `t`.
 * - Support optional form fields that submit empty string "" by returning unions like (T | "").
 *
 * Design notes:
 * - All string-like inputs are trimmed by default to reduce accidental whitespace issues.
 * - Optional form inputs are modeled as `schema.or(z.literal(""))` instead of making the field optional,
 *   because HTML inputs often post empty strings; upstream form parsing can then convert "" to undefined/null if needed.
 * - `convert` hooks allow caller-defined transformations (e.g., lowercasing URLs, normalizing strings) before constraints.
 * - For localized content, Russian (ru) and English (en) fields share identical validators to keep parity across locales.
 */
export const zt = {
  // Plain passthrough for an object factory to keep a single import surface.
  object: z.object,

  /**
   * A schema helper function for validating a boolean type using Zod.
   * Provides optional or required validation based on the `required` flag.
   *
   * @param {AnyTranslationsFn} t - A translation function used to fetch error messages.
   * @param {boolean} [required] - Optional flag to specify whether the boolean value is required.
   * @returns A Zod schema for boolean validation, configured as required or optional.
   */
  boolean: (t: AnyTranslationsFn, required?: boolean) => {
    // Required -> disallow optional.
    if (required) return z.boolean().nonoptional({ error: t(`required`) });
    return z.boolean();
  },

  /**
   * Creates a Zod enum schema with optional transformation and requirement settings.
   *
   * @param {AnyTranslationsFn} t - A function to handle translations for error messages.
   * @param {readonly string[]} variants - An array of string values representing the enum variants.
   * @param {boolean} [required] - A flag indicating if the field is required. Defaults to false, making the field optional and nullable.
   * @param {(d: string) => string} [convert] - An optional function to transform the enum values. Defaults to converting values to uppercase.
   * @returns A Zod enum schema with transformation and optional/nullable behavior based on the parameters provided.
   */
  enum: (
    t: AnyTranslationsFn,
    variants: readonly string[],
    required?: boolean,
    convert?: (d: string) => string,
  ) => {
    const errorMessage = required ? t(`required`) : undefined;

    if (convert) {
      const field = z.enum(variants, { error: errorMessage }).transform(convert);
      if (!required) return field.optional().nullable();
      return field;
    }
    const field = z.enum(variants, { error: errorMessage });

    if (!required) return field.optional().nullable();
    return field;
  },

  /**
   * Validates a trimmed string with optional required/min/max constraints.
   *
   * Behavior:
   * - If `required=true` -> the string must be non-empty.
   * - If `required=false/undefined` -> allows empty string "" in addition to a valid string.
   * - `convert` runs before constraints (e.g., normalize case, strip HTML, etc.).
   *
   * Localization:
   * - Uses translation keys: "required", "min", "max" with interpolation objects where needed.
   *
   * Important:
   * - Passing `min=null` or `max=null` disables that constraint.
   * - CURRENT CHECK STYLE: `if (min) ...` and `if (max) ...` treat 0 as "do not apply".
   *   If you need to allow 0 explicitly, switch checks to `if (min != null)` and `if (max != null)`.
   */
  string: (
    t?: AnyTranslationsFn,
    required?: boolean,
    min?: number | null,
    max?: number | null,
    convert?: (d: ZodString) => ZodString,
  ) => {
    let field = z.string({ error: required ? (t?.(`required`) ?? "Required") : undefined }).trim();

    // Allow the caller to inject additional transformations (slugify, lowercase, etc.).
    if (convert) field = convert(field);

    // Required -> disallow empty strings.
    if (required) field = field.nonempty({ error: t?.(`required`) ?? "Required" });

    // NOTE: 0 is treated as "skip" because of truthy checks; see "Important" in the doc above.
    if (min) field = field.min(min, { error: t?.(`min`, { length: min }) ?? `Min length: ${min}` });
    if (max) field = field.max(max, { error: t?.(`max`, { length: max }) ?? `Max length: ${max}` });

    // HTML-aware content validation: ignore HTML tags and non-breaking spaces when checking emptiness/min/max.
    // Examples considered empty: "<p></p>", "<div>\u00A0</div>", etc.
    field = field.superRefine((val, ctx) => {
      const visible = contentTools.getSanitizedContent(val);

      const len = visible ? visible.length : 0;

      if (required && len === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t?.(`required`) ?? "Required" });
      }
      if (min && len < min) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.(`min`, { length: min }) ?? `Min length: ${min}`,
        });
      }
      if (max && len > max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.(`max`, { length: max }) ?? `Max length: ${max}`,
        });
      }
    });

    // When not required, accept the empty-string case (common for optional text inputs in forms).
    // Rich-text editors serialize an empty document as markup such as "<p></p>". Normalize any
    // visually empty string through a typed union so optional rich text remains optional without
    // widening every form schema input to unknown.
    if (!required) {
      const visuallyEmptyField = z
        .string()
        .refine((value) => !contentTools.getSanitizedContent(value))
        .transform(() => "");

      return field.or(visuallyEmptyField).optional().nullable();
    }
    return field;
  },

  /**
   * Validates a number with optional min/max range constraints.
   *
   * Behavior:
   * - Uses coercing to automatically convert string inputs to numbers (handles HTML input type="number").
   * - If `required=false/undefined`, also allows the empty string "" in union,
   *   which plays well with HTML inputs that submit "" for empty numeric fields.
   *
   * Localization:
   * - Uses translation keys: "minNumber" and "maxNumber" (see note below).
   *
   * Notes:
   * - The "required" branch is intentionally disabled because Zod "optional/nonoptional" on numbers
   *   interacts with parsing; instead, required-ness is modeled via union with "" when not required.
   * - CURRENT CHECK STYLE: `if (min != null)` and `if (max != null)` to support 0 as a valid bound.
   */
  number: (t: AnyTranslationsFn, required?: boolean, min?: number | null, max?: number | null) => {
    let field = z.coerce.number({ error: required ? t(`required`) : undefined });

    // For required fields, reject NaN values (which occur when undefined/null is coerced)
    if (required) {
      field = field.refine((val) => !isNaN(val), {
        message: t(`required`),
      });
    }

    if (min !== null && min !== undefined)
      field = field.gte(min, { error: t(`minNumber`, { number: min }) });
    if (max !== null && max !== undefined)
      field = field.lte(max, { error: t(`maxNumber`, { number: max }) });
    if (!required) return field.or(z.literal("")).optional().nullable();

    return field;
  },

  /**
   * Validates a URL with the HTTPS-only policy.
   *
   * Behavior:
   * - Enforces protocol to be strictly "https" (security requirement).
   * - Trims surrounding whitespace before validation.
   * - If `required=false/undefined`, allows the empty string "" in addition to a valid URL.
   * - `convert` hook can apply normalization (e.g., lowercase host, remove tracking params).
   *
   * Localization:
   * - Uses translation keys: "url" and "required".
   *
   * Security rationale:
   * - Locking to HTTPS mitigates accidental use of insecure endpoints from user inputs.
   */
  url: (t: AnyTranslationsFn, required?: boolean, convert?: (d: ZodURL) => ZodURL) => {
    let field = z.url({ protocol: /^https$/, error: t(`url`) }).trim();

    if (convert) field = convert(field);
    if (required) field = field.nonempty({ error: t(`required`) });
    if (!required) return field.or(z.literal("")).optional().nullable();

    return field;
  },

  /**
   * Creates a validation schema for an email field using the zod library.
   *
   * @param {AnyTranslationsFn} t - A function that provides translations for the validation error messages.
   * @param {boolean} [required] - Determines if the email field is required. Defaults to optional if not provided.
   * @returns A Zod schema object for validating email inputs. If `required` is false or not provided,
   *                      the field allows empty strings, null, or undefined values.
   */
  email: (t: AnyTranslationsFn, required?: boolean) => {
    const field = z.email({
      message: t(`email`),
    });

    if (!required) return field.or(z.literal("")).optional().nullable();
    return field;
  },

  /**
   * Validates a date with optional min/max range constraints.
   *
   * Behavior:
   * - Uses coercing to automatically convert string inputs to dates (handles HTML input type="date").
   * - If `required=false/undefined`, also allows the empty string "" in union,
   *   which plays well with HTML inputs that submit "" for empty date fields.
   *
   * Localization:
   * - Uses translation keys: "required", "minDate" and "maxDate" (see note below).
   *
   * Notes:
   * - The "required" branch validates that the date is not invalid (NaN).
   * - CURRENT CHECK STYLE: `if (min != null)` and `if (max != null)` to support dates as valid bounds.
   */
  date: (t: AnyTranslationsFn, required?: boolean, min?: Date | null, max?: Date | null) => {
    let field = z.coerce.date({ error: required ? t(`required`) : undefined });

    // For required fields, reject invalid dates (which occur when undefined/null is coerced)
    if (required) {
      field = field.refine((val) => !isNaN(val.getTime()), {
        message: t(`required`),
      });
    }

    if (min !== null && min !== undefined)
      field = field.min(min, { error: t(`minDate`, { date: min }) });
    if (max !== null && max !== undefined)
      field = field.max(max, { error: t(`maxDate`, { date: max }) });
    if (!required) return field.or(z.literal("")).optional().nullable();

    return field;
  },

  /**
   * Validates a localized string object with parity between "ru" and "en".
   *
   * Use cases:
   * - Multilingual content where both locales should follow the same constraints.
   * - Optionality is driven by `required`; when false, both fields also allow "" via the underlying helper.
   */
  localizeString: (
    t: AnyTranslationsFn,
    required?: boolean,
    min?: number | null,
    max?: number | null,
  ) =>
    z.object({
      ru: zt.string(t, required, min, max),
      en: zt.string(t, required, min, max),
    }),

  /**
   * Validates a localized URL object with parity between "ru" and "en".
   *
   * Notes:
   * - In many domains URLs are locale-agnostic; prefer `invariantUrl` if the link is inherently the same.
   * - `convert` can be used to normalize URLs (e.g., `.toLowerCase()` on host).
   */
  localizeUrl: (t: AnyTranslationsFn, required?: boolean, convert?: (d: ZodURL) => ZodURL) =>
    z.object({
      ru: zt.url(t, required, convert),
      en: zt.url(t, required, convert),
    }),

  /**
   * Wraps provided `shape` under "data" and made it required.
   *
   * Context:
   * - Aligns with CMS/content backends that structure payloads as { data: { ... } } (e.g., Squidex).
   * - Keeps external interface stable if internal shape evolves.
   */
  content: (shape: unknown) =>
    z
      .object({
        // "data" presence is mandatory to match downstream consumers (renderers, DTO mappers).
        data: z.object(shape as never).required(),
      })
      .required(),

  /**
   * Wraps provided `shape` under "data" and made it optional.
   *
   * Context:
   * - Aligns with CMS/content backends that structure payloads as { data: { ... } } (e.g., Squidex).
   * - Keeps external interface stable if internal shape evolves.
   */
  contentOptional: (shape: unknown) =>
    z
      .object({
        // "data" presence is mandatory to match downstream consumers (renderers, DTO mappers).
        data: z
          .object(shape as never)
          .optional()
          .nullable(),
      })
      .optional()
      .nullable(),

  /**
   * Constructs a Zod schema object for a localized content field with validation rules.
   *
   * @param {AnyTranslationsFn} t - Translation function for generating localized strings.
   * @param {string} field - The field name to include in the schema.
   * @param {boolean} [required] - Indicates if the field is required (default is optional).
   * @param {number | null} [min] - The minimum length for the string field (optional).
   * @param {number | null} [max] - The maximum length for the string field (optional).
   * @returns A Zod schema object incorporating the specified validation rules.
   */
  contentStringField: (
    t: AnyTranslationsFn,
    field: string,
    required?: boolean,
    min?: number | null,
    max?: number | null,
  ) =>
    zt.object({
      content: zt.contentOptional({
        [field]: zt.localizeString(t, required, min, max),
      }),
    }),

  /**
   * Creates a Zod object schema with a single string field and applies validation rules.
   *
   * @param {AnyTranslationsFn} t - A function used for translations in validation messages.
   * @param {string} field - The name of the string field to be defined in the schema.
   * @param {boolean} [required] - Optional. Determines if the string field is required.
   * @param {number | null} [min] - Optional. The minimum length allowed for the string field. Defaults to null if not provided.
   * @param {number | null} [max] - Optional. The maximum length allowed for the string field. Defaults to null if not provided.
   * @returns {ZodObject} Returns a Zod object schema containing the defined string field with applied validation rules.
   */
  stringField: (
    t: AnyTranslationsFn,
    field: string,
    required?: boolean,
    min?: number | null,
    max?: number | null,
  ): ZodObject =>
    zt.object({
      [field]: zt.string(t, required, min, max),
    }),

  /**
   * Validates a single "invariant" URL that is not localized.
   *
   * When to use:
   * - The link is the same for all languages (e.g., canonical site URL).
   *
   * Structure:
   * - Uses the "iv" (invariant) convention to distinguish from "ru"/"en" localized objects.
   */
  invariantUrl: (t: AnyTranslationsFn, required?: boolean, convert?: (d: ZodURL) => ZodURL) =>
    z.object({
      iv: zt.url(t, required, convert),
    }),

  /**
   * Validates and constructs a Zod schema with the provided shape for required invariant property.
   *
   * @function invariant
   * @param {AnyTranslationsFn} t - A translation function to handle localization for error messages.
   * @param {T} shape - The shape defining the structure of the 'iv' property in the schema.
   */
  invariant: <T extends z.ZodType>(t: AnyTranslationsFn, shape: T) =>
    z.object({ iv: shape }).nonoptional({ error: t(`required`) }),

  /**
   * Validates and constructs a Zod schema with the provided shape for optional invariant property.
   *
   * @function invariantOptional
   * @param {T} shape - The shape defining the structure of the 'iv' property in the schema.
   */
  invariantOptional: <T extends z.ZodType>(shape: T) =>
    z
      .union([
        z.object({ iv: shape }),
        z
          .object({ iv: z.null() })
          .strict()
          .transform(() => null),
        z
          .object({ iv: z.literal("") })
          .strict()
          .transform(() => null),
        z
          .object({ iv: z.undefined() })
          .strict()
          .transform(() => null),
        z
          .object({})
          .strict()
          .transform(() => null),
        z.null(),
      ])
      .optional(),
};

// Re-export Zod namespace for convenience to keep the import count minimal across modules.
export { z as z };
